import { useMemo } from 'react';
import { EventNameDisplay } from 'src/components/Events/EventInfo';
import { EventDeletedIcon } from 'src/components/Events/EventStatus/EventDeletedIcon';
import { Stack } from 'src/core/ui';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { ParkingPassIcon } from 'src/svgs/Viagogo/functional-svgs/ParkingPass';
import {
  EVENT_TYPE_PARKING,
  getFormattedEventName,
} from 'src/utils/eventWithDataUtils';
import {
  ApiEventStatus,
  Event,
  Performer,
  TopLevelCategory,
  Venue,
} from 'src/WebApiController';

import * as styles from './EventsTableColumnsConfig.css';

type EventDisplayCellProps = {
  event: Event;
  performer?: Performer;
  venue?: Venue;
  isSinglePerformerView?: boolean;
  forceDisplayEventName?: boolean;
};

export const EventDisplayCell: React.FC<EventDisplayCellProps> = ({
  event,
  performer,
  venue,
  isSinglePerformerView,
  forceDisplayEventName,
}) => {
  const [eventName, eventNameConnector, eventSubName, displayVenueOnly] =
    useMemo(() => {
      const eventName = event.shortName || event.name;
      const lowerCasedEventName = eventName.toLocaleLowerCase();
      const lowerCasedPerformer = performer?.name?.toLocaleLowerCase();
      const displayVenueOnly = forceDisplayEventName
        ? false
        : isSinglePerformerView &&
          (event.genre === TopLevelCategory.Concert ||
            event.genre === TopLevelCategory.Theatre) &&
          lowerCasedEventName === lowerCasedPerformer;

      const [
        formattedEventName,
        formattedEventConnector,
        formattedEventSubName,
      ] = getFormattedEventName({
        event,
        performer,
        venue,
        slimMode: isSinglePerformerView,
        useShortName: true,
      });

      return [
        formattedEventName,
        formattedEventConnector,
        formattedEventSubName,
        displayVenueOnly,
      ];
    }, [event, forceDisplayEventName, isSinglePerformerView, performer, venue]);

  return (
    <TableCellDiv align="left" title={event.name}>
      <Stack justifyContent="spaceBetween" width={'full'}>
        {!displayVenueOnly && (
          <EventNameDisplay
            eventName={eventName}
            eventNameConnector={eventNameConnector}
            eventSubName={eventSubName}
            showTitle={false}
            boldSubName={event.genre === TopLevelCategory.Sports}
          />
        )}
        {event.evStat === ApiEventStatus.Deleted && <EventDeletedIcon />}
        {event.evType === EVENT_TYPE_PARKING && (
          <ParkingPassIcon className={styles.eventDisplayIcon} />
        )}
      </Stack>
    </TableCellDiv>
  );
};
